import { AssignButton, ownerWhere } from "@/components/AssignButton";
import { Countdown, DaysRing, DemoStatusBadge, OutcomeBadge } from "@/components/DemoBits";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { EmptyState, Loading, Stat, Tabs } from "@/components/ui/misc";
import { type Me, type ProspectListItem, openDemoWindow, q } from "@/lib/api";
import { cn, fmtDate, fmtDateTime, relative, stageLabel } from "@/lib/utils";
import type { ProspectDTO } from "@mm-sales/shared";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Play, PlusCircle, Search, Settings } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";

// The three tabs, plus one view per stat card (clicking a card filters the list).
type Filter =
  | "active"
  | "all"
  | "closed"
  | "demos" // Active demos: open prospects with a live, unexpired demo
  | "opened" // Opened by prospect: demo built in the last 30 days that the prospect opened
  | "won"
  | "lost" // Lost / missed
  | "decided"; // Win rate: everything won, lost or missed
const CARD_LABEL: Partial<Record<Filter, string>> = {
  demos: "Active demos",
  opened: "Opened by prospect",
  won: "Won",
  lost: "Lost / missed",
  decided: "Won, lost and missed (win rate)",
};
const PAGE_SIZE = 20;
const SCOPE_KEY = "mm-sales:scope";

// What each view asks the api for (see GET /v1/prospects).
const queryFor = (filter: Filter): Record<string, string | undefined> => {
  switch (filter) {
    case "all":
      return { includeClosed: "true" };
    case "closed":
    case "decided":
      return { closed: "true" };
    case "demos":
      return { demo: "active" };
    case "opened":
      return { demo: "opened", includeClosed: "true" };
    case "won":
      return { outcome: "won" };
    case "lost":
      return { outcome: "lost,missed" };
    default:
      return {};
  }
};

export function DashboardPage() {
  const me = useOutletContext<Me>();
  const qc = useQueryClient();
  const [filter, setFilter] = useState<Filter>("active");
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [page, setPage] = useState(1);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);
  useEffect(() => setPage(1), [filter, debounced]);
  // Clicking the selected card again returns to the plain Active list.
  const pickCard = (f: Filter) => setFilter((cur) => (cur === f ? "active" : f));
  const cardLabel = CARD_LABEL[filter];

  // Superadmins see their own account's prospects by default and may switch to
  // everyone's; the choice sticks per browser. Everyone else only ever has "mine".
  const isSuperadmin = me.agent.role === "superadmin";
  const [scope, setScopeState] = useState<"mine" | "all">(() => {
    try {
      return localStorage.getItem(SCOPE_KEY) === "all" ? "all" : "mine";
    } catch {
      return "mine";
    }
  });
  const setScope = (v: "mine" | "all") => {
    setScopeState(v);
    setPage(1);
    try {
      localStorage.setItem(SCOPE_KEY, v);
    } catch {
      /* private mode */
    }
  };
  const scopeParam = isSuperadmin && scope === "all" ? "all" : undefined;
  const metrics = useQuery({
    queryKey: ["metrics", scopeParam],
    queryFn: () => q.metrics({ scope: scopeParam }),
  });
  // Server-side paging: the api filters, sorts by last activity and slices.
  const prospects = useQuery({
    queryKey: ["prospects", filter, debounced, page, scopeParam],
    queryFn: () =>
      q.prospects({
        q: debounced,
        scope: scopeParam,
        ...queryFor(filter),
        page,
        pageSize: PAGE_SIZE,
      }),
    placeholderData: keepPreviousData,
  });
  const items = prospects.data?.items ?? [];
  const total = prospects.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="p-6 lg:p-8">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Welcome, {me.agent.name.split(" ")[0]}
          </h1>
          <p className="text-sm text-muted-foreground">Your prospects and their demos.</p>
        </div>
        <div className="flex items-center gap-2">
          {me.agent.role === "superadmin" ? (
            <Link to="/app/settings">
              <Button variant="outline">
                <Settings className="h-4 w-4" /> Settings
              </Button>
            </Link>
          ) : null}
          <Link to="/app/new">
            <Button>
              <PlusCircle className="h-4 w-4" /> New demo
            </Button>
          </Link>
        </div>
      </div>

      {metrics.data ? (
        <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-6">
          <Stat
            label="Open"
            value={metrics.data.prospects.open}
            onClick={() => setFilter("active")}
            active={filter === "active"}
          />
          <Stat
            label="Active demos"
            value={metrics.data.demos.activeNow}
            hint={
              metrics.data.demos.expiringSoon
                ? `${metrics.data.demos.expiringSoon} expiring in 2 days`
                : "provisioned, not expired"
            }
            tone={metrics.data.demos.expiringSoon ? "warning" : undefined}
            onClick={() => pickCard("demos")}
            active={filter === "demos"}
          />
          <Stat
            label="Opened by prospect"
            value={`${metrics.data.demos.openRate}%`}
            hint={`${metrics.data.demos.opened} of ${metrics.data.demos.created} (30d)`}
            onClick={() => pickCard("opened")}
            active={filter === "opened"}
          />
          <Stat
            label="Won"
            value={metrics.data.prospects.won}
            tone="success"
            hint="last 30 days"
            onClick={() => pickCard("won")}
            active={filter === "won"}
          />
          <Stat
            label="Lost / missed"
            value={metrics.data.prospects.lost + metrics.data.prospects.missed}
            tone={metrics.data.prospects.missed ? "destructive" : undefined}
            hint={`${metrics.data.prospects.missed} missed`}
            onClick={() => pickCard("lost")}
            active={filter === "lost"}
          />
          <Stat
            label="Win rate"
            value={`${metrics.data.prospects.winRate}%`}
            hint="won, lost and missed"
            onClick={() => pickCard("decided")}
            active={filter === "decided"}
          />
        </div>
      ) : null}

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Tabs
          value={cardLabel ? "" : filter}
          onChange={(v) => setFilter((v || "active") as Filter)}
          items={[
            { value: "active", label: "Active" },
            { value: "closed", label: "Closed" },
            { value: "all", label: "All" },
          ]}
        />
        {isSuperadmin ? (
          <Tabs
            value={scope}
            onChange={(v) => setScope(v === "all" ? "all" : "mine")}
            items={[
              { value: "mine", label: "Only mine" },
              { value: "all", label: "Everyone" },
            ]}
          />
        ) : null}
        {cardLabel ? (
          <button
            type="button"
            onClick={() => setFilter("active")}
            className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/10 px-3 py-1 text-xs font-medium text-primary hover:bg-primary/20"
            title="Clear this filter"
          >
            Showing: {cardLabel} <span aria-hidden>×</span>
          </button>
        ) : null}
        <div className="relative ml-auto w-64">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search business, city, email"
            className="pl-8"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {prospects.isLoading ? (
        <Loading />
      ) : items.length === 0 ? (
        <EmptyState
          title={
            debounced ? "No matches" : cardLabel ? `Nothing under "${cardLabel}"` : "No demos yet"
          }
          body="Build a personalized demo from a prospect's Google listing or website in under a minute."
          action={
            <Link to="/app/new">
              <Button>
                <PlusCircle className="h-4 w-4" /> New demo
              </Button>
            </Link>
          }
        />
      ) : (
        <div
          className={cn(
            "overflow-hidden rounded-lg border bg-card",
            prospects.isFetching && "opacity-70",
          )}
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-2.5 text-left font-semibold">Business</th>
                  {isSuperadmin ? (
                    <th className="px-4 py-2.5 text-left font-semibold">Account</th>
                  ) : null}
                  <th className="px-4 py-2.5 text-left font-semibold">Stage</th>
                  <th className="px-4 py-2.5 text-left font-semibold">Demo</th>
                  <th className="px-4 py-2.5 text-left font-semibold">Timeline</th>
                  <th className="px-4 py-2.5 text-left font-semibold">Demo time left</th>
                  <th
                    className="px-4 py-2.5 text-left font-semibold"
                    title="Time left to follow up before the prospect is taken from the account"
                  >
                    Follow-up window
                  </th>
                  <th className="px-4 py-2.5 text-left font-semibold">Last activity</th>
                  <th className="px-4 py-2.5 text-right font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {items.map((p) => (
                  <ProspectRow
                    key={p.id}
                    p={p}
                    showAccount={isSuperadmin}
                    onChanged={() => {
                      qc.invalidateQueries({ queryKey: ["prospects"] });
                      qc.invalidateQueries({ queryKey: ["metrics"] });
                    }}
                  />
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between border-t px-4 py-2.5 text-xs text-muted-foreground">
            <span>
              {total === 0
                ? "No prospects"
                : `${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, total)} of ${total}`}
            </span>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((x) => Math.max(1, x - 1))}
              >
                <ChevronLeft className="h-3.5 w-3.5" /> Prev
              </Button>
              <span className="px-2 tabular-nums">
                {page} / {pages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= pages}
                onClick={() => setPage((x) => Math.min(pages, x + 1))}
              >
                Next <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ProspectRow({
  p,
  showAccount,
  onChanged,
}: { p: ProspectListItem; showAccount: boolean; onChanged: () => void }) {
  const demo = p.activeDemo;
  const live = demo && demo.status === "active";
  return (
    <tr className="hover:bg-muted/30">
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div
            className="h-9 w-9 shrink-0 overflow-hidden rounded-md bg-muted"
            style={{
              background: p.heroImageUrl ? undefined : `${p.brandColor ?? "#174ae7"}33`,
            }}
          >
            {p.heroImageUrl ? (
              <img src={p.heroImageUrl} alt="" className="h-full w-full object-cover" />
            ) : null}
          </div>
          <div className="min-w-0">
            <Link
              to={`/app/prospects/${p.id}`}
              className="block truncate font-semibold hover:underline"
            >
              {p.businessName}
            </Link>
            <div className="truncate text-xs text-muted-foreground">
              {[p.cuisine, [p.city, p.state].filter(Boolean).join(", ")]
                .filter(Boolean)
                .join(" · ") || "—"}
            </div>
          </div>
        </div>
      </td>
      {showAccount ? (
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="min-w-0">
              <div className="truncate font-medium">{p.owner?.name ?? "Unknown account"}</div>
              <div className="truncate text-xs text-muted-foreground">
                {(p.owner && ownerWhere(p.owner)) || "—"}
              </div>
            </div>
            <AssignButton
              prospectId={p.id}
              currentOwner={p.ownerAgentId}
              onDone={onChanged}
              size="sm"
              variant="ghost"
            />
          </div>
        </td>
      ) : null}
      <td className="px-4 py-3">
        <Badge variant="outline">{stageLabel[p.stage] ?? p.stage}</Badge>
      </td>
      <td className="px-4 py-3">
        {p.outcome !== "open" ? (
          // Hover: when it was decided, the amount entered and the note.
          <span
            className="inline-flex cursor-help items-center gap-1.5"
            title={[
              `${outcomeWord(p.outcome)} ${p.closedAt ? `on ${fmtDateTime(p.closedAt)}` : ""}`.trim(),
              p.value ? `Amount: ${money(p.value)}` : "Amount: not entered",
              p.lostReason ? `Note: ${p.lostReason}` : "Note: none",
            ].join("\n")}
          >
            <OutcomeBadge outcome={p.outcome} />
            {p.value ? (
              <span className="text-xs font-medium tabular-nums">{money(p.value)}</span>
            ) : null}
          </span>
        ) : demo ? (
          <DemoStatusBadge status={demo.status} />
        ) : (
          <Badge variant="secondary">No demo</Badge>
        )}
      </td>
      <td className="px-4 py-3">
        <Timeline p={p} />
      </td>
      <td className="px-4 py-3">
        {live ? (
          <DaysRing expiresAt={demo.expiresAt} createdAt={p.createdAt} />
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>
      <td className="px-4 py-3">
        <FollowUpWindow p={p} />
      </td>
      <td className="px-4 py-3 text-muted-foreground">{relative(p.lastActivityAt)}</td>
      <td className="px-4 py-3">
        <div className="flex justify-end gap-2">
          {live ? (
            <Button size="sm" onClick={() => openDemoWindow(demo.shareToken)}>
              <Play className="h-3.5 w-3.5" /> Launch
            </Button>
          ) : null}
          <Link to={`/app/prospects/${p.id}`}>
            <Button size="sm" variant="outline">
              <Settings className="h-3.5 w-3.5" /> Settings
            </Button>
          </Link>
        </div>
      </td>
    </tr>
  );
}

/**
 * Time left before the prospect leaves the account: no activity by the deadline
 * moves a partner's prospect to the internal team and marks anyone else's missed.
 */
function FollowUpWindow({ p }: { p: ProspectListItem }) {
  if (p.outcome !== "open" || !p.slaDeadlineAt)
    return <span className="text-muted-foreground">—</span>;
  const left = new Date(p.slaDeadlineAt).getTime() - Date.now();
  const then = p.owner?.kind === "partner" ? "moves to the internal team" : "is marked missed";
  return (
    <div
      title={`Without activity by ${fmtDateTime(p.slaDeadlineAt)} this prospect ${then}.`}
      className="cursor-help"
    >
      <Countdown
        to={p.slaDeadlineAt}
        compact
        className={cn(
          "font-medium",
          left <= 0 ? "text-destructive" : left <= 2 * 86_400_000 ? "text-amber-600" : undefined,
        )}
      />
      <div className="text-xs text-muted-foreground">{left <= 0 ? "overdue" : `then ${then}`}</div>
    </div>
  );
}

const outcomeWord = (o: ProspectDTO["outcome"]) =>
  o === "won" ? "Won" : o === "lost" ? "Lost" : o === "missed" ? "Missed" : "Open";

const money = (n: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n);

/**
 * The prospect's history at a glance: when it was created, when its demo was built,
 * and when it was decided. Every step is also logged in the prospect's Activity tab.
 */
function Timeline({ p }: { p: ProspectDTO }) {
  const steps: { label: string; at: string; tone?: "success" | "destructive" }[] = [
    { label: "Created", at: p.createdAt },
  ];
  if (p.activeDemo?.createdAt) steps.push({ label: "Demo built", at: p.activeDemo.createdAt });
  if (p.outcome !== "open" && p.closedAt)
    steps.push({
      label: outcomeWord(p.outcome),
      at: p.closedAt,
      tone: p.outcome === "won" ? "success" : "destructive",
    });
  return (
    <div className="space-y-0.5 text-xs">
      {steps.map((s) => (
        <div
          key={s.label}
          className="flex items-center gap-2 whitespace-nowrap"
          title={fmtDateTime(s.at)}
        >
          <span
            className={cn(
              "w-[68px] text-muted-foreground",
              s.tone === "success" && "font-medium text-success",
              s.tone === "destructive" && "font-medium text-destructive",
            )}
          >
            {s.label}
          </span>
          <span className="tabular-nums">{fmtDate(s.at)}</span>
        </div>
      ))}
    </div>
  );
}
